import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Pause, Play, Play as Run, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { dateIt, money } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";

const FREQ_KEYS = {
  weekly: "rec.weekly",
  monthly: "rec.monthly",
  quarterly: "rec.quarterly",
  yearly: "rec.yearly",
};

const Ricorrenti = () => {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);

  const load = () => api.get("/recurrings").then(({ data }) => setRows(data));

  useEffect(() => {
    load();
  }, []);

  const update = async (id, patch) => {
    // aggiornamento immediato in pagina, poi conferma dal server
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    try {
      await api.put(`/recurrings/${id}`, patch);
      load();
    } catch {
      toast.error(t("rem.failed"));
      load();
    }
  };

  const runNow = async (id) => {
    try {
      const { data } = await api.post(`/recurrings/${id}/run-now`);
      toast.success(`${t("rec.issued")} ${data.number}`);
      load();
    } catch {
      toast.error(t("rem.failed"));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/recurrings/${id}`);
      toast.success(t("rec.deleted"));
    } catch {
      toast.error(t("rem.failed"));
    }
    load();
  };

  return (
    <div className="space-y-5" data-testid="recurring-page">
      <p className="max-w-2xl text-sm text-slate-500">{t("rec.hint")}</p>

      <div className="card-flat overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="px-4 py-3">{t("common.client")}</th>
              <th className="px-4 py-3">{t("rec.frequency")}</th>
              <th className="px-4 py-3">{t("rec.next")}</th>
              <th className="px-4 py-3 text-right">{t("common.total")}</th>
              <th className="px-4 py-3 text-right">{t("rec.count")}</th>
              <th className="px-4 py-3">{t("rec.autoSend")}</th>
              <th className="px-4 py-3 text-right">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody data-testid="recurring-table">
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-slate-500">
                  {t("rec.empty")}{" "}
                  <Link to="/fatture" className="font-semibold text-blue-600">
                    {t("nav.invoices")}
                  </Link>
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr
                key={r.id}
                className={`border-b border-slate-100 transition-colors duration-200 hover:bg-slate-50 ${
                  r.active ? "" : "opacity-50"
                }`}
              >
                <td className="px-4 py-3 font-medium">
                  {r.template?.client?.name || "-"}
                  <span className="font-mono-num ml-2 text-xs text-slate-400">
                    {r.source_number}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <select
                    data-testid={`rec-frequency-${r.id}`}
                    value={r.frequency}
                    onChange={(e) => update(r.id, { frequency: e.target.value })}
                    className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm"
                  >
                    {Object.entries(FREQ_KEYS).map(([key, label]) => (
                      <option key={key} value={key}>
                        {t(label)}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="font-mono-num px-4 py-3">{dateIt(r.next_run)}</td>
                <td className="font-mono-num px-4 py-3 text-right font-semibold">
                  {money(r.template?.total || 0, r.template?.currency)}
                </td>
                <td className="font-mono-num px-4 py-3 text-right text-slate-600">
                  {r.created_count}
                </td>
                <td className="px-4 py-3">
                  <input
                    data-testid={`rec-autosend-${r.id}`}
                    type="checkbox"
                    checked={!!r.auto_send}
                    onChange={(e) => update(r.id, { auto_send: e.target.checked })}
                    className="h-4 w-4 rounded border-slate-300"
                  />
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1">
                    <button
                      data-testid={`rec-run-${r.id}`}
                      onClick={() => runNow(r.id)}
                      title={t("rec.runNow")}
                      className="rounded-md p-2 text-slate-500 transition-colors duration-200 hover:bg-emerald-600 hover:text-white"
                    >
                      <Run className="h-3.5 w-3.5" />
                    </button>
                    <button
                      data-testid={`rec-toggle-${r.id}`}
                      onClick={() => update(r.id, { active: !r.active })}
                      title={r.active ? t("rec.pause") : t("rec.resume")}
                      className="rounded-md p-2 text-slate-500 transition-colors duration-200 hover:bg-slate-900 hover:text-white"
                    >
                      {r.active ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                    </button>
                    <button
                      data-testid={`rec-delete-${r.id}`}
                      onClick={() => remove(r.id)}
                      className="rounded-md p-2 text-slate-500 transition-colors duration-200 hover:bg-red-600 hover:text-white"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <button
        onClick={load}
        data-testid="rec-reload"
        className="flex items-center gap-2 text-xs font-semibold text-slate-500 hover:text-slate-900"
      >
        <RefreshCw className="h-3.5 w-3.5" /> {t("common.reload")}
      </button>
    </div>
  );
};

export default Ricorrenti;
